import {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';
import {
  BreadcrumbList,
  WithContext
} from 'schema-dts';
import { MainLayout } from '@/components';
import {
  getBreadcrumbLinks,
  getPageInfo
} from '@/config/pages';
import { ShopDetailBlog } from '@/features/shops/components/pages/ShopDetail/ShopDetailBlog';
import {
  ShopForRerakuBlogQuery,
  useShopForRerakuBlogQuery,
} from '@/graphql/generated/graphql';
import { NextPage } from '@/types/nextjs';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const Page: NextPage<Props> = ({ shop }: Props) => {
  return (
    <ShopDetailBlog
      shop={shop}
    />
  );
};

Page.getLayout = (page) => {
  const { shop } = page.props;
  const jsonLd: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'name': 'パンくずリスト',
    'itemListElement': [{
      '@type': 'ListItem',
      'position': 1,
      'item': {
        '@id': getPageInfo('top', {}, { withFullUrl: true }).href,
        'name': getPageInfo('top', {}).name
      }
    }, {
      '@type': 'ListItem',
      'position': 2,
      'item': {
        '@id': getPageInfo('studio', {}, { withFullUrl: true }).href,
        'name': getPageInfo('studio', {}).name
      }
    }, {
      '@type': 'ListItem',
      'position': 3,
      'item': {
        '@id': getPageInfo('studio/{slug}', shop, { withFullUrl: true }).href,
        'name': getPageInfo('studio/{slug}', shop).name
      }
    }, {
      '@type': 'ListItem',
      'position': 4,
      'item': {
        '@id': getPageInfo('studio/{slug}/blog', shop, { withFullUrl: true }).href,
        'name': getPageInfo('studio/{slug}/blog', shop).name
      }
    }]
  };

  return (
    <MainLayout
      metaTagInfo={{
        title: `${shop.name}のブログ一覧 - ${page.props.page}ページ目`,
        description: `${shop.name}のブログ一覧です。マッサージ・整体ファンに大人気の肩甲骨ストレッチ【WEB予約OK】`,
        canonicalUrl: getPageInfo('studio/{slug}/blog', shop, {
          withFullUrl: true,
          params: { page: String(page.props.page) },
          excludeFirstPageParam: true
        }).href,
      }}
      breadcrumbLinks={getBreadcrumbLinks('studio/{slug}/blog', shop, {
        'top': {},
        'studio': {},
        'studio/{slug}': shop,
      })}
      jsonLd={JSON.stringify(jsonLd)}
      og={{
        'og:title': `${shop.name}のブログ一覧 - ${page.props.page}ページ目`,
        'og:description': `${shop.name}のブログ一覧です。マッサージ・整体ファンに大人気の肩甲骨ストレッチ【WEB予約OK】`,
      }}
    >
      {page}
    </MainLayout>
  );
};

type ServerProps = {
  shop: NonNullable<ShopForRerakuBlogQuery['shop']>;
  page: number;
}

export const getServerSideProps: GetServerSideProps<ServerProps> = async (ctx: GetServerSidePropsContext) => {
  const slug = ctx.query.slug;
  const page = ctx.query.page ? Number(ctx.query.page) : 1;

  const data = await useShopForRerakuBlogQuery.fetcher({ page, per: 20, slug: String(slug) })();
  if (!data.shop) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      shop: data.shop,
      page,
    }
  };
};

export default Page;
